import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import Api from 'fou-api';
import { AI_AGREEMENT_TYPE, AI_RESPONSE_ERROR_TYPES, AI_TAGS_FEEDBACK } from 'src/utils/constants/common.js';
import i18n from 'src/assets/i18n/i18n';
import { Toaster } from 'src/utils/helper/toaster';

const { t } = i18n().global;

export function useAiTextEditor() {
  // Store
  const store = useStore();
  // Refs
  const feedbackGiven = ref(false);

  const hasRoleCompute = computed(() => store.getters['auth/hasRole']);

  const hasConsentedToAi = computed({
    get: () => store.getters['profile/getFoundryAiConsent'],
    set: value => {
      store.commit('profile/setFoundryAiConsent', value);
    }
  });
  const aiAgreement = computed({
    get: () => window.localStorage.getItem(AI_AGREEMENT_TYPE),
    set: value => {
      window.localStorage.setItem(AI_AGREEMENT_TYPE, value);
    }
  });

  // methods
  /**
   * The function `aiSuggestionCallback` processes a payload to make an AI suggestion request and handles
   * the response accordingly, including error handling.
   * @returns The `aiSuggestionCallback` function returns an object with the following structure:
   * ```javascript
   * {
   *   success: boolean,
   *   data: response data or null,
   *   error: {
   *     code: AI_RESPONSE_ERROR_TYPES,
   *     message: error message
   *   }
   * }
   * ```
   */
  const aiSuggestionCallback = async payload => {
    if (!payload) {
      return null;
    }

    const { action, description, assetType } = payload;
    if (!action || !description || !assetType) {
      return null;
    }

    const result = {
      success: false,
      data: null,
      error: {
        code: AI_RESPONSE_ERROR_TYPES.general,
        message: t('pages.aiTextEditor.ai_suggestion_error')
      }
    };

    try {
      const response = await Api.Ai.aiDescription(payload);
      result.success = true;
      result.data = response.data;
    } catch (error) {
      result.success = false;
      result.data = null;
      if (error?.response?.data?.error?.errorType === 'RATE_LIMIT_EXCEEDED') {
        result.error.code = AI_RESPONSE_ERROR_TYPES.rateLimit;
      }
      if (error?.response?.data?.error?.message) {
        result.error.message = error?.response?.data?.error?.message;
      }
    }
    return result;
  };

  /**
   * The function `updateFeedback` updates the value of `feedbackGiven` based on the input `feedback`.
   * @returns The function `updateFeedback` is returning the value of `feedbackGiven.value`.
   */
  const updateFeedback = async params => {
    const { feedbackId, feedback = '', isAccept = false } = params;

    if (!feedback && !isAccept) {
      feedbackGiven.value = false;
      return;
    }
    feedbackGiven.value = feedback;
    try {
      const feedbackPayload = {};
      if (feedback) {
        feedbackPayload.feedback = feedback;
      }
      if (typeof isAccept === 'boolean' && isAccept) {
        feedbackPayload.isAccept = true;
      }
      await Api.Ai.submitAiFeedback(feedbackId, feedbackPayload);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
    return feedbackGiven.value;
  };

  /**
   * Fire-and-forget submit of AI tags qualitative feedback.
   * @param {Object} params
   * @param {'submission'|'inventorySubmission'} params.assetType
   * @param {string} params.assetId
   * @param {string} params.message
   * @param {string} [params.foundryId]
   */
  const submitAiTagsFeedback = ({ assetType, assetId, message, foundryId }) => {
    const trimmed = message?.trim();
    if (!trimmed || !assetId) {
      return;
    }

    const payload = {
      assetType,
      assetId,
      action: AI_TAGS_FEEDBACK.action,
      suggestionType: AI_TAGS_FEEDBACK.suggestionType,
      metadata: { message: trimmed }
    };

    if (foundryId) {
      payload.foundryId = foundryId;
    }

    Api.Ai.createAiFeedback(payload).catch((error) => {
      console.error('Error submitting AI tags feedback:', error);
      Toaster.notifyError(t('pages.aiTagsFeedback.submit_error'));
    });
  };

  /**
   * The function `getAiAgreement` retrieves AI agreement data either from a local variable or from an
   * API call and returns the result.
   * @returns The `getAiAgreement` function returns a Promise that resolves to an object with the
   * following structure:
   * ```javascript
   * {
   *   success: boolean,
   *   data: object
   * }
   * ```
   * The `success` property indicates whether the operation was successful or not, and the `data`
   * property contains the retrieved AI agreement data if successful.
   */
  const getAiAgreement = async () => {
    const result = {
      success: false,
      data: null
    };
    try {
      if (aiAgreement.value) {
        result.success = true;
        result.data = JSON.parse(aiAgreement.value);
      } else {
        const response = await Api.FeatureAgreements.getFeatureAgreements(AI_AGREEMENT_TYPE);
        if (response.data.success && response.data?.data?.length > 0) {
          result.success = true;
          result.data = response.data.data[0];
          aiAgreement.value = JSON.stringify(response.data.data[0]);
        }
      }
    } catch (error) {
      console.error('Error occurred in getting the Ai Agreement', error);
    }
    return result;
  };

  /**
   * The function `updateAiConsentForFoundry` updates AI consent for a Foundry session using the provided
   * feature agreement ID.
   * @returns The function `updateAiConsentForFoundry` is returning a boolean value indicating whether
   * the update operation was successful or not.
   */
  const updateAiConsentForFoundry = async featureAgreementId => {
    let success = false;
    try {
      const sessionFoundryId = window.localStorage.getItem('sessionFoundryId');
      const payload = {
        featureAgreementId
      };
      const response = await Api.Foundry.giveAiConsent(sessionFoundryId, payload);
      success = response?.data?.success || false;
    } catch (error) {
      console.error('Error occurred in updating foundry Ai Consent', error);
    }
    return success;
  };

  return {
    aiSuggestionCallback,
    updateFeedback,
    submitAiTagsFeedback,
    feedbackGiven,
    getAiAgreement,
    hasConsentedToAi,
    updateAiConsentForFoundry,
    aiAgreement,
    hasRoleCompute
  };
}
